import React from "react";
import { gql } from "@apollo/client";
import { useQuery, useLazyQuery } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import {
  FaSyncAlt, FaSearch, FaBell, FaChartLine, FaClipboardList, FaUsers,
  FaUserCircle, FaPlane, FaCogs, FaServicestack, FaTools,
  FaCompass, FaShoppingCart, FaAddressBook, FaCreditCard, FaUserGraduate
} from "react-icons/fa";
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import "../styles/Dashboard.css";

import Cards from "./Cards";
import Charts from "./Charts";
import Activity from "./Activity";

const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($year: Int) {
    getDashboardStats {
      totalSellers
      approvedSellers
      pendingSellers
      rejectedSellers
      totalBuyers
      approvedBuyers
      pendingBuyers
      rejectedBuyers
      totalOrders
      totalServices
      totalRentals
      totalTrainings
    }
    getBuyerTrends(year: $year) {
      date
      count
    }
    getRecentActivity {
      id
      type
      title
      timestamp
      icon
    }
  }
`;

const GET_ENTITY_DETAILS = gql`
  query GetEntityDetails($type: String!, $status: String, $id: ID) {
    getEntityDetails(type: $type, status: $status, id: $id) {
      id
      name
      email
      status
      createdAt
    }
  }
`;

const GLOBAL_SEARCH = gql`
  query GlobalSearch($query: String!) {
    globalSearch(query: $query) {
      results {
        ... on DroneSearchResult { id type name brand }
        ... on PartSearchResult { id type name brand }
        ... on AccessorySearchResult { id type name brand }
        ... on BuyerSearchResult { id type firstName lastName email }
        ... on SellerSearchResult { id type businessName email }
        ... on OrderSearchResult { id type orderId customerName status }
        ... on ServiceSearchResult { id type name category }
        ... on RentalSearchResult { id type droneName category }
        ... on TrainingSearchResult { id type title category }
        ... on PageSearchResult { id type title route }
      }
    }
  }
`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showSearchResults, setShowSearchResults] = React.useState(false);
  const searchRef = React.useRef(null);

  const { loading, error, data } = useQuery(GET_DASHBOARD_STATS, {
    variables: { year: selectedYear },
    pollInterval: 30000,
    fetchPolicy: 'network-only'
  });

  const [showNotifications, setShowNotifications] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const lastActivityId = React.useRef(null);
  const notificationRef = React.useRef(null);

  const [openDialog, setOpenDialog] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState("");
  const [getDetails, { loading: detailsLoading, data: detailsData }] = useLazyQuery(GET_ENTITY_DETAILS, {
    fetchPolicy: 'network-only'
  });

  const [executeSearch, { data: searchData, loading: searchLoading }] = useLazyQuery(GLOBAL_SEARCH);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Detect new activities for notification badge
  React.useEffect(() => {
    const activities = data?.getRecentActivity || [];
    if (activities.length > 0) {
      const latestId = activities[0].id;
      if (lastActivityId.current && latestId !== lastActivityId.current) {
        if (!showNotifications) {
          setUnreadCount(prev => prev + 1);
        }
      }
      lastActivityId.current = latestId;
    }
  }, [data?.getRecentActivity, showNotifications]);

  const handleNotificationToggle = () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      setUnreadCount(0);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length > 2) {
      executeSearch({ variables: { query: value } });
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  const getResultIcon = (res) => {
    const type = res.type?.toUpperCase();
    if (type === 'PAGE') {
      const title = res.title?.toLowerCase() || "";
      if (title.includes('address')) return <FaAddressBook />;
      if (title.includes('cart')) return <FaShoppingCart />;
      if (title.includes('tax')) return <FaCreditCard />;
      if (title.includes('training')) return <FaUserGraduate />;
      if (title.includes('job') || title.includes('pilot')) return <FaPlane />;
      if (title.includes('order') || title.includes('sold')) return <FaClipboardList />;
      return <FaCompass />;
    }

    switch (type) {
      case 'BUYER': return <FaUsers />;
      case 'SELLER': return <FaUserCircle />;
      case 'ORDER': return <FaClipboardList />;
      case 'DRONE':
      case 'RENTAL': return <FaPlane />;
      case 'PART': return <FaCogs />;
      case 'ACCESSORY': return <FaTools />;
      case 'SERVICE': return <FaServicestack />;
      case 'TRAINING': return <FaUserGraduate />;
      default: return <FaChartLine />;
    }
  };

  const getResultLabel = (res) => {
    const type = res.type?.toUpperCase();
    switch (type) {
      case 'BUYER': return `${res.firstName || ''} ${res.lastName || ''} (Buyer)`.trim();
      case 'SELLER': return `${res.businessName || res.companyName || res.name || 'Unnamed'} (Seller)`;
      case 'ORDER': return `Order #${res.orderId || res.id} - ${res.status || 'Pending'}`;
      case 'SERVICE': return `${res.name} (Service)`;
      case 'RENTAL': return `${res.droneName || res.name} (Rental)`;
      case 'TRAINING': return `${res.title || res.name} (Training)`;
      case 'PAGE': return `${res.title} (Page)`;
      case 'DRONE':
      case 'PART':
      case 'ACCESSORY':
        return `${res.brand ? res.brand + ' ' : ''}${res.name || res.model || 'Product'}`;
      default: return res.name || res.title || res.businessName || res.companyName || "Unnamed Result";
    }
  };

  const handleSearchResultClick = (res) => {
    setSearchTerm("");
    setShowSearchResults(false);

    // Connect search result to the respective overall files/pages
    const type = res.type?.toUpperCase();

    if (type === 'PAGE' && res.route) {
      navigate(res.route);
      return;
    }

    switch (type) {
      case 'SELLER': navigate('/seller-details'); break;
      case 'BUYER': navigate('/RegisteredBuyer'); break;
      case 'ORDER': navigate('/orders'); break;
      case 'SERVICE': navigate('/services'); break;
      case 'RENTAL': navigate('/rentals'); break;
      case 'TRAINING': navigate('/training-page'); break;
      case 'DRONE': navigate('/drones'); break;
      case 'PART': navigate('/parts'); break;
      case 'ACCESSORY': navigate('/accessories'); break;
      default:
        // Fallback to the dashboard detail view if no specific page
        handleCardClick(getResultLabel(res), res.type.toLowerCase());
    }
  };

  const handleCardClick = (title, type, status, id) => {
    if (!type) return;

    // Ensure accurate backend entity mapping
    let backendType = type;
    if (type.toLowerCase() === "buyer") backendType = "Buyer";
    else if (type.toLowerCase() === "seller") backendType = "Seller";
    else if (type.toLowerCase() === "order") backendType = "Order";
    else if (type.toLowerCase() === "service") backendType = "Service";
    else if (type.toLowerCase() === "rental") backendType = "Rental";
    else if (type.toLowerCase() === "training") backendType = "Training";
    else if (type.toLowerCase() === "drone") backendType = "Drone";
    else if (type.toLowerCase() === "part") backendType = "Part";
    else if (type.toLowerCase() === "accessory") backendType = "Accessory";
    else backendType = "Activity";

    setDialogTitle(title || "Details");
    setOpenDialog(true);
    getDetails({ variables: { type: backendType, status, id } });
  };

  if (loading && !data) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f7fa' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <Alert severity="error" sx={{ borderRadius: '25px' }}>{error.message}</Alert>
    </Container>
  );

  const stats = data?.getDashboardStats || {};
  const buyerTrends = data?.getBuyerTrends || [];
  const recentActivity = data?.getRecentActivity || [];

  return (
    <div className="dashboard-main-wrapper">
      {/* Boltz Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Dashboard Overview</h1>
        </div>

        <div className="header-search" ref={searchRef}>
          <FaSearch color="#718EBF" />
          <input
            type="text"
            placeholder="Search report or analytics..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => searchTerm.length > 2 && setShowSearchResults(true)}
          />
          {showSearchResults && (
            <div className="search-results-dropdown">
              {searchLoading ? (
                <div className="search-status">Searching...</div>
              ) : searchData?.globalSearch?.results?.length > 0 ? (
                searchData.globalSearch.results.map((res) => (
                  <div
                    key={`${res.type}-${res.id}`}
                    className="search-result-item"
                    onClick={() => handleSearchResultClick(res)}
                  >
                    <div className="result-icon">
                      {getResultIcon(res)}
                    </div>
                    <div className="result-info">
                      <div className="result-name">{getResultLabel(res)}</div>
                      <div className="result-meta">
                        {res.brand || res.category ? (
                          <span>{res.brand || res.category}</span>
                        ) : null}
                      </div>
                      <div className="result-type">{res.type}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="search-status">No results found for "{searchTerm}"</div>
              )}
            </div>
          )}
        </div>

        <div className="header-right">
          <div className="icon-btn" onClick={() => window.location.reload()} title="Reload Page">
            <FaSyncAlt />
          </div>

          <div className="icon-btn-container" ref={notificationRef}>
            <div className="icon-btn" onClick={handleNotificationToggle} title="Notifications">
              <FaBell />
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </div>

            {showNotifications && (
              <div className="notifications-dropdown">
                <div className="notification-header">
                  <h3>Notifications</h3>
                  <div
                    style={{ fontSize: '0.75rem', color: '#2d60ff', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => setUnreadCount(0)}
                  >
                    Mark all as read
                  </div>
                </div>
                {recentActivity.length > 0 ? (
                  recentActivity.map((notif) => (
                    <div
                      key={notif.id}
                      className="notification-item"
                      onClick={() => {
                        handleCardClick(notif.title, notif.type, null, notif.id);
                        setShowNotifications(false);
                      }}
                    >
                      <div className="notif-icon">
                        {getResultIcon({ type: notif.type })}
                      </div>
                      <div className="notif-content">
                        <div className="notif-title">{notif.title}</div>
                        <div className="notif-time">
  {notif.timestamp ? (
    <>
      {new Date(notif.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      {' • '}
      {new Date(notif.timestamp).toLocaleDateString()}
    </>
  ) : (
    'Just now'
  )}
</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-notifications">No new updates</div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <Grid container spacing={4}>
        {/* TOP SECTION: Platform Metrics */}
        <Grid size={12}>
          <Box sx={{ mb: 1 }}>
            <span className="section-label">Platform Metrics</span>
            <Cards stats={stats} onCardClick={handleCardClick} />
          </Box>
        </Grid>

        {/* MAIN ANALYTICAL GRID: Two-Column Vertical Flow */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Box>
              <span className="section-label">User Growth Trends</span>
              <Charts
                stats={stats}
                trends={buyerTrends}
                type="buyer"
                onItemClick={handleCardClick}
                selectedYear={selectedYear}
                onYearChange={setSelectedYear}
              />
            </Box>
            <Box>
              <span className="section-label">Marketplace Status</span>
              <Charts stats={stats} type="seller" onItemClick={handleCardClick} />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Box>
              <span className="section-label">Recent platform activity</span>
              <Activity activities={recentActivity} onItemClick={handleCardClick} />
            </Box>
            <Box>
              <span className="section-label">Overall Performance</span>
              <Charts stats={stats} type="overall" onItemClick={handleCardClick} />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '25px', padding: '1.5rem' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
          <Typography variant="h5" fontWeight={700} color="#343c6a">{dialogTitle}</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ border: 'none', px: 3 }}>
          {detailsLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <div className="entity-details-list">
              <div className="details-header-row">
                <div className="col-id">UID</div>
                <div className="col-name">Name</div>
                <div className="col-email">Email Address</div>
                <div className="col-status">Status</div>
              </div>
              {(detailsData?.getEntityDetails || []).map((item, index) => (
                <div key={item.id || index} className="details-data-row">
                  <div className="col-id">{item.id || 'N/A'}</div>
                  <div className="col-name">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="avatar-mini">{item.name?.charAt(0).toUpperCase() || '?'}</div>
                      <span style={{ fontWeight: 600 }}>{item.name || "N/A"}</span>
                    </div>
                  </div>
                  <div className="col-email">{item.email}</div>
                  <div className="col-status">
                    <span className={`status-badge ${item.status?.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!detailsData?.getEntityDetails || detailsData.getEntityDetails.length === 0) && (
                <Box sx={{ py: 4, textAlign: 'center', color: '#718ebf' }}>No records found for this category.</Box>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="contained"
            sx={{
              bgcolor: '#2d60ff',
              borderRadius: '50px',
              textTransform: 'none',
              px: 5,
              py: 1.2,
              fontWeight: 600,
              boxShadow: '0 8px 16px rgba(45, 96, 255, 0.2)'
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}